import React, { useState, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Camera, X, Check, Edit3 } from 'lucide-react';
import Tesseract from 'tesseract.js';

interface OCRScannerProps {
  isOpen: boolean;
  onClose: () => void;
  onTextScanned: (text: string) => void;
}

export const OCRScanner: React.FC<OCRScannerProps> = ({ isOpen, onClose, onTextScanned }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [scannedText, setScannedText] = useState('');
  const [liveText, setLiveText] = useState('');
  const [textBlocks, setTextBlocks] = useState<Array<{
    text: string;
    x: number;
    y: number;
    width: number;
    height: number;
    confidence: number;
  }>>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [permissionError, setPermissionError] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editableText, setEditableText] = useState('');
  const [videoScale, setVideoScale] = useState({ x: 1, y: 1 });
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scanIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Detect if running on mobile/iOS Safari
  const isMobileSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                        (navigator.userAgent.includes('Safari') && /Mobi|Android/i.test(navigator.userAgent));

  // iPhone-like text scanning with advanced image processing
  const performLiveScan = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || isProcessing) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx || video.videoWidth === 0 || video.videoHeight === 0) {
      return;
    }

    try {
      setIsProcessing(true);
      
      // High resolution for iPhone-like quality
      const resolution = 1920;
      const videoAspect = video.videoWidth / video.videoHeight;
      let canvasWidth, canvasHeight;
      
      if (video.videoWidth > video.videoHeight) {
        canvasWidth = resolution;
        canvasHeight = resolution / videoAspect;
      } else {
        canvasHeight = resolution;
        canvasWidth = resolution * videoAspect;
      }
      
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      // High quality rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw full frame
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Apply advanced image preprocessing
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const enhanced = enhanceImageForOCR(imageData);
      ctx.putImageData(enhanced, 0, 0);

      // Convert to high quality blob
      canvas.toBlob(async (blob) => {
        if (!blob) {
          setIsProcessing(false);
          return;
        }

        try {
          // Advanced OCR configuration similar to iPhone
          const ocrOptions = {
            logger: (m: any) => {
              if (m.status === 'recognizing text') {
                console.log(`OCR Progress: ${Math.round(m.progress * 100)}%`);
              }
            },
            tessedit_pageseg_mode: '6', // Uniform block of text
            tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyzäöüÄÖÜß0123456789.,!?- ',
            tessedit_ocr_engine_mode: '1', // Neural nets LSTM engine
          };

          const result = await Tesseract.recognize(blob, 'deu+eng', ocrOptions);
          const detectedText = result.data.text.trim();
          const confidence = result.data.confidence || 0;
          
          console.log('OCR Result:', { text: detectedText, confidence });

          // Less strict validation - more like iPhone
          const cleanText = detectedText.replace(/[^\w\säöüÄÖÜß\-\.\,\!\?]/g, ' ').trim();
          const validWords = cleanText.split(/\s+/).filter(word => word.length >= 2);
          
          const isValidText = cleanText && 
                             cleanText.length >= 2 && 
                             confidence > 30 &&
                             validWords.length >= 1 &&
                             /[a-zA-ZäöüÄÖÜß]/.test(cleanText);

          if (isValidText) {
            console.log('✅ Text detected:', cleanText);
            setLiveText(cleanText);
            
            // Create text block overlay
            const textBlocks = [{
              text: cleanText,
              x: canvas.width * 0.1,
              y: canvas.height * 0.3,
              width: canvas.width * 0.8,
              height: canvas.height * 0.4,
              confidence
            }];
            
            setTextBlocks(textBlocks);
          } else {
            setTextBlocks([]);
            setLiveText('');
          }
        } catch (error) {
          console.error('OCR Error:', error);
        } finally {
          setTimeout(() => setIsProcessing(false), 100);
        }
      }, 'image/jpeg', 0.95);
    } catch (error) {
      console.error('Scan Error:', error);
      setIsProcessing(false);
    }
  }, [isProcessing]);

  // Advanced image enhancement for better OCR (iPhone-like processing)
  const enhanceImageForOCR = (imageData: ImageData) => {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;
    const enhanced = new ImageData(width, height);
    
    // Convert to grayscale and enhance contrast
    for (let i = 0; i < data.length; i += 4) {
      // Grayscale conversion with luminance weighting
      const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      
      // Adaptive contrast enhancement
      const contrast = 1.5;
      const brightness = 10;
      let enhanced_value = (gray - 128) * contrast + 128 + brightness;
      
      // Apply adaptive thresholding for better text recognition
      if (enhanced_value > 140) {
        enhanced_value = Math.min(255, enhanced_value * 1.1);
      } else if (enhanced_value < 100) {
        enhanced_value = Math.max(0, enhanced_value * 0.8);
      }
      
      enhanced_value = Math.max(0, Math.min(255, enhanced_value));
      
      enhanced.data[i] = enhanced_value;     // R
      enhanced.data[i + 1] = enhanced_value; // G
      enhanced.data[i + 2] = enhanced_value; // B
      enhanced.data[i + 3] = data[i + 3];    // A
    }
    
    return enhanced;
  };

  // Image sharpening filter for better text recognition
  const applySharpenFilter = (imageData: ImageData) => {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;
    const sharpened = new ImageData(width, height);
    
    // Sharpening kernel
    const kernel = [
      0, -1, 0,
      -1, 5, -1,
      0, -1, 0
    ];
    
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        let r = 0, g = 0, b = 0;
        
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const idx = ((y + ky) * width + (x + kx)) * 4;
            const k = kernel[(ky + 1) * 3 + (kx + 1)];
            r += data[idx] * k;
            g += data[idx + 1] * k;
            b += data[idx + 2] * k;
          }
        }
        
        const idx = (y * width + x) * 4;
        sharpened.data[idx] = Math.max(0, Math.min(255, r));
        sharpened.data[idx + 1] = Math.max(0, Math.min(255, g));
        sharpened.data[idx + 2] = Math.max(0, Math.min(255, b));
        sharpened.data[idx + 3] = data[idx + 3]; // Alpha
      }
    }
    
    return sharpened;
  };

  // Manual scan function
  const performManualScan = useCallback(async () => {
    if (isProcessing) return;
    await performLiveScan();
  }, [performLiveScan, isProcessing]);

  // Fast scanning like iPhone
  const startLiveScanning = useCallback(async () => {
    if (scanIntervalRef.current || !isVideoReady) return;

    setIsScanning(true);
    
    // Fast scanning interval for real-time recognition
    const scanInterval = 800;
    
    scanIntervalRef.current = setInterval(() => {
      if (!isProcessing) {
        performLiveScan();
      }
    }, scanInterval);
    
    // Initial scan
    setTimeout(() => {
      if (!isProcessing && isVideoReady) {
        performLiveScan();
      }
    }, 500);
  }, [isVideoReady, performLiveScan, isProcessing]);

  // Stop live scanning
  const stopLiveScanning = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    setIsScanning(false);
    setLiveText('');
    setTextBlocks([]);
  }, []);

  const startCamera = useCallback(async () => {
    if (stream) return;
    
    try {
      setIsVideoReady(false);
      setPermissionError(false);
      
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720 },
          frameRate: { ideal: 30 }
        }
      });
      
      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        
        videoRef.current.onloadedmetadata = () => {
          setIsVideoReady(true);
        };
        
        videoRef.current.play().catch((error) => {
          console.error('Error playing video:', error);
        });
      }
    } catch (error) {
      console.error('Error starting camera:', error);
      setIsVideoReady(false);
      
      if (error.name === 'NotAllowedError') {
        setPermissionError(true);
      }
    }
  }, [stream]);

  const stopCamera = useCallback(() => {
    stopLiveScanning();
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsVideoReady(false);
    setPermissionError(false);
  }, [stream, stopLiveScanning]);

  // Handle accepting detected text
  const handleAcceptText = () => {
    if (liveText) {
      setEditableText(liveText);
      setShowEditDialog(true);
    }
  };

  // Handle final confirmation with error protection
  const handleFinalConfirm = () => {
    try {
      if (editableText.trim()) {
        console.log('🎯 OCR: Confirming text:', editableText.trim());
        onTextScanned(editableText.trim());
      }
      handleClose();
    } catch (error) {
      console.error('💥 OCR: Error in final confirm:', error);
      // Still close to prevent getting stuck
      handleClose();
    }
  };

  const handleClose = () => {
    stopCamera();
    setScannedText('');
    setLiveText('');
    setTextBlocks([]);
    setEditableText('');
    setIsProcessing(false);
    setIsVideoReady(false);
    setPermissionError(false);
    setIsScanning(false);
    setShowEditDialog(false);
    onClose();
  };

  const handleCloseEditDialog = () => {
    setShowEditDialog(false);
    setEditableText('');
  };

  // Start live scanning when video is ready
  React.useEffect(() => {
    if (isVideoReady && !isScanning) {
      startLiveScanning();
    }
  }, [isVideoReady, isScanning, startLiveScanning]);

  React.useEffect(() => {
    if (isOpen && !permissionError) {
      startCamera();
    }
    
    if (!isOpen) {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [isOpen, permissionError]);

  return (
    <>
      {/* Main Camera Dialog */}
      <Dialog open={isOpen && !showEditDialog} onOpenChange={handleClose}>
        <DialogContent className="max-w-sm p-0 overflow-hidden">
          <div className="relative">
            {permissionError ? (
              <div className="w-full h-64 bg-black flex flex-col items-center justify-center text-white p-4">
                <Camera className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-center text-sm mb-4">Kamera-Zugriff verweigert</p>
                <p className="text-xs text-center opacity-75">Bitte Kamera-Berechtigung in den Browser-Einstellungen erlauben</p>
              </div>
            ) : (
              <>
                <video
                  ref={videoRef}
                  className="w-full h-64 object-cover bg-black"
                  autoPlay
                  playsInline
                  muted
                  style={{ opacity: isVideoReady ? 1 : 0 }}
                />
                
                {!isVideoReady && (
                  <div className="absolute inset-0 bg-black flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent" />
                  </div>
                )}

                {/* iPhone-like Text Overlay */}
                {textBlocks.length > 0 && (
                  <div className="absolute inset-0 pointer-events-none">
                    {textBlocks.map((block, index) => {
                      const videoElement = videoRef.current;
                      if (!videoElement) return null;
                      
                      const scaleX = videoElement.offsetWidth / videoElement.videoWidth;
                      const scaleY = videoElement.offsetHeight / videoElement.videoHeight;
                      
                      return (
                        <div
                          key={index}
                          className="absolute border border-blue-400 bg-blue-400/20 backdrop-blur-sm rounded"
                          style={{
                            left: `${(block.x * scaleX)}px`,
                            top: `${(block.y * scaleY)}px`,
                            width: `${Math.max(block.width * scaleX, 20)}px`,
                            height: `${Math.max(block.height * scaleY, 16)}px`,
                          }}
                        >
                          <div className="absolute -top-5 left-0 bg-blue-500 text-white text-xs px-1 py-0.5 rounded text-nowrap max-w-32 truncate">
                            {block.text}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Status display at top */}
                {liveText && (
                  <div className="absolute top-4 left-4 right-4 z-10">
                    <div className="bg-green-500/90 backdrop-blur-sm text-white p-2 rounded-lg shadow-lg">
                      <div className="flex items-center gap-2">
                        <Check className="h-4 w-4" />
                        <span className="text-sm font-medium">Text erkannt</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Processing indicator */}
                {isProcessing && (
                  <div className="absolute top-4 left-4 right-4">
                    <div className="bg-blue-500/90 text-white p-2 rounded-lg shadow-lg">
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
                        <span className="text-sm">Analysiere Text...</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Scanning indicator */}
                {isScanning && !liveText && !isProcessing && (
                  <div className="absolute top-4 left-4 right-4">
                    <div className="bg-blue-500/90 text-white p-2 rounded-lg shadow-lg">
                      <div className="flex items-center gap-2">
                        <div className="animate-pulse h-2 w-2 bg-white rounded-full" />
                        <span className="text-sm">Suche nach Text...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <canvas ref={canvasRef} className="hidden" />
                
                {/* Controls */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleClose}
                    className="bg-black/50 hover:bg-black/70 text-white border-0"
                  >
                    <X className="h-4 w-4" />
                    Schließen
                  </Button>
                  
                  {liveText && (
                    <Button
                      onClick={handleAcceptText}
                      className="bg-green-500 hover:bg-green-600 text-white border-0"
                      size="sm"
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Übernehmen
                    </Button>
                  )}
                </div>
                
                {/* Overlay guide when no text detected */}
                {!liveText && !isProcessing && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="border-2 border-white/50 rounded-lg w-3/4 h-16 flex items-center justify-center">
                      <span className="text-white/70 text-sm">Text hier positionieren</span>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Text Dialog */}
      <Dialog open={showEditDialog} onOpenChange={handleCloseEditDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="h-5 w-5" />
              Text bearbeiten
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Erkannter Text:
              </label>
              <Input
                value={editableText}
                onChange={(e) => setEditableText(e.target.value)}
                placeholder="Text hier bearbeiten..."
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && editableText.trim()) {
                    handleFinalConfirm();
                  }
                }}
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleCloseEditDialog}
                className="flex-1"
              >
                <X className="h-4 w-4 mr-2" />
                Abbrechen
              </Button>
              
              <Button
                onClick={handleFinalConfirm}
                disabled={!editableText.trim()}
                className="flex-1"
              >
                <Check className="h-4 w-4 mr-2" />
                Einfügen
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};